export const APP_NAME = 'Takshify';

export const brand = {
  name: APP_NAME,
  tagline: 'Learn Indian Knowledge Systems',
  punchline: 'The Digital Takshashila',
  subtitle:
    'Takshify is a premium learning platform for Parashari Jyotish, Jaimini, and the full spectrum of Indian Knowledge Systems - taught by masters, powered by AI.',
} as const;

export const aboutPage = {
  title: 'About Takshify',
  metaDescription:
    'Takshify is building the Digital Takshashila - preserving and teaching Indian Knowledge Systems from Parashari Jyotish to Ayurveda, Yoga, and Vedanta.',
  heritage: `Takshify draws its inspiration from Takshashila (Taxila), one of the world's oldest and most renowned centers of learning, where seekers gathered to study diverse disciplines-from astronomy and philosophy to medicine, governance, and mathematics. We believe that spirit of open, interconnected learning deserves to thrive again in the digital age.`,
  vision: `Our vision is to build a Digital Takshashila-a single destination where timeless Indian Knowledge Systems are preserved, taught, practiced, and explored through modern technology and AI. Beginning with Parashari Jyotish and Jaimini, Takshify will gradually expand into KP Astrology, Bhrigu Nandi Nadi, Panchang, Sanskrit, Ayurveda, Yoga, Vedanta, Numerology, Vastu, and many other classical disciplines.`,
  video: {
    title: 'Where ancient gurukul meets modern learning',
    caption: 'A glimpse of the Digital Takshashila we are building for sadhaks worldwide.',
    ariaLabel: 'Takshify platform overview video',
    highlights: [
      'Structured syllabi rooted in classical texts and guru lineages',
      'Interactive visualizations, live batches, and AskGuru AI mentorship',
      'A sangha of sadhaks learning Jyotish and Indian Knowledge Systems together',
    ],
  },
  sadhak: `Takshify is not just a place to consume content-it is a place to grow as a Sadhak. Through structured learning paths, authentic teachings, interactive visualizations, AI mentors, and a vibrant community, we aim to make ancient wisdom accessible, practical, and relevant for generations to come.`,
  closing: 'Takshify is building the Digital Takshashila for the next generation of Sadhaks.',
  disciplines: [
    'Parashari Jyotish',
    'Jaimini',
    'KP Astrology',
    'Bhrigu Nandi Nadi',
    'Panchang',
    'Sanskrit',
    'Ayurveda',
    'Yoga',
    'Vedanta',
    'Numerology',
    'Vastu',
  ],
} as const;

export const home = {
  schoolsSectionTitle: 'Schools of Jyotish',
  schoolsSectionSubtitle:
    'Vedic astrology is a family of traditions - each school a different lens on the same sky. Choose your path and begin when you are ready.',
  whyTakshify: {
    title: 'Why Takshify?',
    heritageLabel: 'Ancient Takshashila',
    heritage:
      'Takshify is inspired by Takshashila (Taxila), one of the world\'s oldest and most renowned centers of learning, where scholars from across civilizations came to study philosophy, astronomy, medicine, governance, and many other disciplines.',
    etymologyLabel: 'The craftsman\'s path',
    etymology:
      'The name Taksh comes from the Sanskrit word associated with a skilled craftsman-someone who shapes, refines, and creates with purpose.',
    metaphor:
      'We believe learning is a similar journey of transformation, where knowledge is carefully shaped into understanding and wisdom.',
    mission:
      'At Takshify, we carry forward the spirit of Takshashila by combining authentic Indian Knowledge Systems with the power of AI.',
    method:
      'Every lesson is designed to help you explore, practice, question, and master timeless knowledge through modern, interactive learning.',
    tagline:
      'Takshify is building the Digital Takshashila for the next generation of Sadhaks.',
    expandLabel: 'Why Takshify?',
    collapseLabel: 'Close',
    hoverHint: 'Click to read our story',
  },
  featuredTitle: 'Start with Parashari',
  featuredSubtitle:
    'The mainstream classical path - nine grahas, twelve bhavas, divisional charts, and predictive yogas rooted in Brihat Parashara Hora Shastra.',
  ctaBandTitle: 'Your chart is waiting to be read.',
  ctaBandSubtitle:
    'Join Takshify and begin the marga - structured lessons, daily practice, and a community of fellow seekers.',
  ctaBandButton: 'Sign in to begin',
  coursesSectionTitle: 'Courses',
  coursesSectionSubtitle:
    'Structured curricula across Parashari, Jaimini, and more - taught by masters, paced for serious study.',
  upcomingSectionTitle: 'Upcoming live sessions',
  upcomingSectionSubtitle:
    'Live teaching with your guru - sign in to view schedules and batch details.',
  marketingSignInCta: 'Sign in',
  marketingSignInSyllabusCta: 'View syllabus',
} as const;

export const marketing = {
  stats: [
    { value: '169', label: 'Syllabus topics' },
    { value: '9', label: 'Grahas mastered' },
    { value: '12', label: 'Bhavas studied' },
    { value: 'BPHS', label: 'Classical foundation' },
  ],
  pillarsTitle: 'Built for serious seekers',
  pillarsSubtitle:
    'Ancient wisdom deserves modern structure - without losing the soul of the tradition.',
  pillars: [
    {
      title: 'Structured marga',
      description:
        'A clear path from foundations to prediction - Abhyas and Saar syllabi with milestones, quizzes, and revision queues.',
    },
    {
      title: 'Hands-on charts',
      description:
        'Interactive hora charts, calculations, and case studies - learn by doing, not only by reading.',
    },
    {
      title: 'AskGuru & AI study',
      description:
        'An AI companion grounded in classical syllabus - plus flashcards, revision, and daily quizzes.',
    },
  ],
} as const;

export const auth = {
  loginTitle: 'Welcome back',
  loginSubtitle: 'Sign in with Google or email to continue your Takshify learning journey',
  forgotTitle: 'Reset your password',
  forgotSubtitle: 'Enter your email and we will send you a reset link',
} as const;

export const learnerNav = {
  sidebarTitle: 'My learning',
  /** Sidebar group headings - keep in sync with `constants/learnerNavConfig`. */
  sectionLearn: 'Learn',
  sectionPractice: 'Practice',
  sectionLibrary: 'My Library',
  sectionCommunity: 'Community',
  dashboard: 'My Dashboard',
  courses: 'Schools',
  coursesEnrolled: 'Your courses',
  coursesAvailable: 'Available to enroll',
  coursesAvailableSubtitle: 'Explore more paths and enroll when you are ready.',
  coursesEmptyEnrolled: 'You have not started a course yet. Pick one below to begin.',
  batches: 'My Batches',
  community: 'My Community',
  assessments: 'My Assessments',
  assessmentsSubtitle: 'School practice exams and institute batch assessments.',
  assessmentsSchoolPracticeTitle: 'School practice exams',
  assessmentsBatchTitle: 'Institute batch exams',
  assessmentsBatchEmpty:
    'No assessments assigned yet. Your coordinator or guru will publish exams here.',
  assessmentsUnderPreparation: 'Under preparation',
  assessmentsLive: 'Live',
  assessmentsParashariTitle: 'Parashari Jyotish',
  assessmentsParashariBlurb:
    'Chapter and lesson practice exams - Abhyas foundations plus chart-reading drills.',
  assessmentsParashariPracticeTitle: 'Practice exams',
  calendar: 'My Calendar',
  reminders: 'My Reminders',
  sadhana: 'My Sadhana',
  certificates: 'My Certificates',
  certificatesSampleTitle: 'Sample credential preview',
  certificatesSampleBlurb:
    'This is what a Takshify certificate looks like. The Sample watermark means this is for illustration only - your earned credentials appear below with real verification codes.',
  certificatesEmpty: 'No certificates yet. Complete school assessments to earn yours.',
  certificatesYoursTitle: 'Your credentials',
  programs: 'Programs',
  bookmarks: 'My Bookmarks',
  notes: 'My Notes',
  privateNotes: 'My Private Notes',
  studyMaterials: 'My Handouts',
  aiTutor: 'AskGuru',
  progress: 'My Progress',
  ethics: 'Ethics',
  settings: 'Settings',
  coursesStat: 'Schools started',
} as const;

export const schoolsCatalog = {
  pageTitle: 'Schools',
  heroSubtitle:
    'Distinct schools of thought, knowledge topics, and platform features - each offering a unique path through Indian Knowledge Systems.',
  errorTitle: 'Unable to load the latest catalog',
  errorDescription: 'Showing cached or fallback data. Try again to refresh the catalog.',
  retry: 'Try again',
  availableEmptyTitle: 'No schools available yet',
  availableEmptyDescription: 'Check back soon - new schools of thought are on the way.',
  availableNowTitle: 'Available now',
  platformFeaturesTitle: 'Platform features',
  platformFeaturesSubtitle: 'Tools that support your learning across every school.',
  comingSoonSchoolsTitle: 'Coming soon - schools of thought',
  comingSoonSchoolsSubtitle: 'Divination and predictive traditions on the roadmap.',
  comingSoonTopicsTitle: 'Coming soon - knowledge topics',
  comingSoonTopicsSubtitle: 'Classical IKS subjects beyond astrology.',
  roadmapTitle: 'Explore the roadmap',
  roadmapSubtitle: 'What we are building next across schools, topics, and platform tools.',
  startLearningCta: 'Start Learning',
  viewSyllabusCta: 'View syllabus',
  signInToStartCta: 'Sign in to start learning',
  signInToStartSubtitle: 'Create a free account to read lessons, track progress, and join batches.',
  syllabusPageTitle: 'Syllabus',
  syllabusPageEyebrow: 'Curriculum outline',
  syllabusPreviewNote: 'Topic outline only - sign in to read full lessons.',
  syllabusNotFound: 'Syllabus preview is not available for this school yet.',
  backToSchools: 'Back to schools',
  parashariSyllabusSummary: 'Parashari Jyotish marga at a glance',
  parashariSyllabusNote:
    'Sign in to browse the full Parashari syllabus - topics, lessons, and quizzes across the Jyotish marga.',
  plannedSyllabusTitle: 'Planned curriculum highlights',
  comingSoonLabel: 'Coming Soon',
  availableLabel: 'Available',
} as const;

export const dashboard = {
  pageTitle: 'Dashboard',
  welcome: 'Welcome back',
  welcomeNew: 'Welcome',
  streamsTitle: 'Dashboard',
  streamsSubtitle: 'Pick up where you left off, or see what to learn next.',
  activitiesSubtitle: 'Your progress, upcoming sessions, and recent learning activity.',
  journeyTitle: 'Your journey',
  journeySubtitle: 'Pick up where you left off, or begin your classical Jyotish marga.',
  emptyJourney: 'Your marga awaits - start with Parashari when you are ready.',
  emptyJourneyCta: 'Begin learning',
  continueLearning: 'Continue learning',
  upcomingClasses: 'Upcoming classes',
  enrollCta: 'Request enrollment',
  schoolEnrollCta: 'Enroll',
  enrollDisclaimer:
    'Enrollment is confirmed only when seats are available in your batch.',
  pillarsTitle: 'How Takshify works',
  pillarsSubtitle: 'Courses, batches, and AI-guided study.',
} as const;

/** Learner My Batches (`/batches`) - invite-enrolled institute cohorts. */
export const batchesPage = {
  subtitle:
    'Cohorts grouped by training institute. Open a batch for Guru’s Path, curriculum, and certificates.',
  emptyTitle: 'No institute batches yet',
  emptyDescription:
    'Accept an invite link from your guru to get started. Once enrolled, your cohorts appear here by institute.',
  emptyAction: 'Go to Dashboard',
  errorTitle: 'Unable to load your batches',
  errorDescription:
    'Something went wrong while loading your enrollments. Please try again.',
  retry: 'Try again',
  pastInstitutes: 'Past institutes',
  pastHint:
    'Read-only history from institutes you previously attended. Certificates remain available from each batch.',
  continueTitle: 'Continue learning',
  openBatch: 'Open batch',
  viewHistory: 'View history',
  notStarted: 'Not started',
  progress: 'Progress',
  guru: 'Guru',
  special: 'Special',
  past: 'Past',
  studentsOne: '1 student',
  studentsMany: '{count} students',
  dueOne: '1 due',
  dueMany: '{count} due',
  overdueOne: '1 overdue',
  overdueMany: '{count} overdue',
  nextDue: 'Next due {date}',
  startsToday: 'Starts today',
  startsTomorrow: 'Starts tomorrow',
  startsInDays: 'Starts in {count} days',
  endsToday: 'Ends today',
  endsTomorrow: 'Ends tomorrow',
  endsInDays: 'Ends in {count} days',
  ended: 'Ended',
  statusActive: 'Active',
  statusUpcoming: 'Upcoming',
  statusCompleted: 'Completed',
  statusCancelled: 'Cancelled',
  batchCountOne: '1 batch',
  batchCountMany: '{count} batches',
  loadingLabel: 'Loading your batches',
} as const;

/** Learner batch detail (`/batches/:batchId`). */
export const batchDetailPage = {
  breadcrumbsBatches: 'My Batches',
  notFoundTitle: 'Batch not found',
  notFoundDescription:
    'This batch is unavailable or you are not enrolled. Return to My Batches to see your cohorts.',
  notFoundAction: 'Back to My Batches',
  errorTitle: 'Unable to load this batch',
  errorDescription: 'Something went wrong while loading batch details. Please try again.',
  retry: 'Try again',
  pastBanner:
    'This is a past enrollment. You can still review history, certificates, and revealed answers.',
  liveNow: 'Live now',
  joinClass: 'Join class',
  upcomingClass: 'Next class',
  viewSchedule: 'View schedule',
  assignmentsDue: 'Assignments due',
  viewAssignments: 'View assignments',
  tabGuruPath: "Guru's Path",
  tabLive: 'Live classes',
  tabAssignments: 'Assignments',
  tabResources: 'Resources',
  tabProgress: 'Progress',
  assignmentsEmpty: 'No assignments yet. Your guru will publish work here.',
  resourcesEmpty:
    'No Guru resources for this batch yet. Watermarked institute handouts live under Teaching.',
  resourcesTeachingLink: 'Teaching',
  resourcesLoading: 'Loading resources…',
  resourcesOpen: 'Open',
  resourcesOpening: 'Opening…',
  resourcesOpenFailed: 'Could not open this file. Try again.',
  progressHero: 'Your progress',
  progressEmpty: 'Not started yet',
  progressHint: 'Lesson and path completion for this cohort.',
  progressCta: "Open Guru's Path",
  progressBody:
    'Progress reflects work in this batch. Continue on Guru’s Path for topics, notes, and quizzes.',
  questionsOne: '1 question',
  questionsMany: '{count} questions',
  dueLabel: 'Due {date}',
  gradeLabel: 'Grade: {score}%',
  awaitingGrade: 'Submitted - awaiting Guru grade',
  retakeGranted: 'Your guru granted another attempt.',
  startAssignment: 'Start assignment',
  startRetake: 'Start retake',
  pastDue: 'Past due',
  noQuestions: 'No questions yet',
  submittedAwaiting: 'Submitted - awaiting grade',
  reviewAnswers: 'Review your answers',
  statusPending: 'Pending',
  statusSubmitted: 'Submitted',
  statusGraded: 'Graded',
  loadingLabel: 'Loading batch',
} as const;

/** Learner Bookmarks (`/bookmarks`). */
export const bookmarksPage = {
  subtitle: 'Lessons you saved for quick access later.',
  emptyTitle: 'No bookmarks yet',
  emptyDescription:
    'Save a lesson while studying and it will appear here for quick access.',
  emptyAction: 'Browse schools',
  errorTitle: 'Unable to load your bookmarks',
  errorDescription:
    'Something went wrong while loading your bookmarks. Please try again.',
  retry: 'Try again',
  countOne: '1 bookmark',
  countMany: '{count} bookmarks',
  loadingLabel: 'Loading your bookmarks',
} as const;

/** Learner Calendar (`/calendar`). */
export const calendarPage = {
  subtitle: 'Live classes, assignment due dates, and completed work across your batches.',
  emptyTitle: 'Nothing on your calendar yet',
  emptyDescription:
    'When your gurus schedule live classes or assign work, it appears here. Meanwhile, grow with Takshify assessments.',
  emptyAction: 'Explore assessments',
  emptyRangeBody:
    'No classes or due work in this view. Fill your week with a Takshify assessment.',
  emptyRangeAction: 'Try an assessment',
  emptyRangePastBody: 'No classes or due work in this past period.',
  errorTitle: 'Unable to load your calendar',
  errorDescription:
    'Something went wrong while loading your calendar. Please try again.',
  retry: 'Try again',
  upcoming: 'Upcoming',
  past: 'Past',
  countOne: '1 event',
  countMany: '{count} events',
  loadingLabel: 'Loading your calendar',
  today: 'Today',
  prev: 'Previous',
  next: 'Next',
  week: 'Week',
  month: 'Month',
  viewToggleLabel: 'Calendar view',
  filterLabel: 'Show',
  filterLive: 'Live',
  filterDue: 'Due',
  filterDone: 'Done',
  daySheetTitle: 'Day',
  dayEmptyHint: 'Nothing scheduled',
  dayEmptyBody: 'No live class or due work this day. Keep learning with an assessment.',
  dayEmptyPastBody: 'Nothing was scheduled on this day.',
  dayCount: '{count} on this day',
  close: 'Close',
  changeTimezone: 'Change',
} as const;

/** Learner Reminders (`/reminders`). */
export const remindersPage = {
  subtitle:
    'Sadhana, study, and random-topic reminders configured by your learning preferences.',
  emptyTitle: 'No reminders yet',
  emptyDescription:
    'Reminders you configure for sadhana, study, or topics will appear here.',
  errorTitle: 'Unable to load your reminders',
  errorDescription:
    'Something went wrong while loading your reminders. Please try again.',
  retry: 'Try again',
  countOne: '1 reminder',
  countMany: '{count} reminders',
  loadingLabel: 'Loading your reminders',
  enabled: 'Enabled',
  disabled: 'Disabled',
} as const;

/** Learner Community (`/community`). */
export const communityPage = {
  subtitle: 'Discuss with fellow Sadhaks in your batch channels.',
  emptyTitle: 'No community channels yet',
  emptyDescription:
    'When your institute creates a batch and enables its community, it will appear here. Ask your guru or coordinator if you expected to see one.',
  errorTitle: 'Unable to load your community',
  errorDescription: 'Something went wrong while loading channels. Please try again.',
  retry: 'Try again',
  channelsCountOne: '1 channel',
  channelsCountMany: '{count} channels',
  loadingLabel: 'Loading your community',
  channelsTitle: 'Channels',
  threadsTitle: 'Threads',
  threadsEmpty: 'No threads yet. Start the first conversation.',
  selectThread: 'Select a thread to view',
} as const;

/** Learner Assessments (`/assessments`). */
export const assessmentsPage = {
  errorTitle: 'Unable to load your assessments',
  errorDescription:
    'Something went wrong while loading your assessments. Please try again.',
  retry: 'Try again',
  loadingLabel: 'Loading your assessments',
} as const;

/** Learner Teaching / study materials (`/study-materials`). */
export const studyMaterialsPage = {
  title: 'My Handouts',
  subtitle:
    "Watermarked Takshify handouts published for your batches and programs. Guru file packs live under each batch's Resources tab.",
  emptyTitle: 'No handouts yet',
  emptyDescription: 'Your Guru publishes them from the institute Teaching hub.',
  errorTitle: 'Unable to load handouts',
  errorDescription: 'Something went wrong while loading handouts. Please try again.',
  retry: 'Try again',
  countOne: '1 handout',
  countMany: '{count} handouts',
  loadingLabel: 'Loading handouts',
  download: 'View / Download',
} as const;

/** Learner Notes (`/notes`) - lesson notes captured while studying. */
export const notesPage = {
  subtitle: 'Notes you take while studying lessons appear here, organized by lesson.',
  emptyTitle: 'No lesson notes yet',
  emptyDescription: 'Notes you take while studying will appear here.',
  emptyAction: 'Browse schools',
  errorTitle: 'Unable to load your notes',
  errorDescription: 'Something went wrong while loading your notes. Please try again.',
  retry: 'Try again',
  countOne: '1 note',
  countMany: '{count} notes',
  loadingLabel: 'Loading your notes',
} as const;

/** Learner Private Notes (`/private-notes`) - personal, unshared notes. */
export const privateNotesPage = {
  subtitle: 'Personal study notes only you can see - not tied to a lesson.',
  emptyTitle: 'No private notes yet',
  emptyDescription: 'Create one to capture your personal study thoughts.',
  errorTitle: 'Unable to load your private notes',
  errorDescription: 'Something went wrong while loading your notes. Please try again.',
  retry: 'Try again',
  newNote: 'New note',
  titleLabel: 'Title (optional)',
  titlePlaceholder: 'Note title',
  contentLabel: 'Note',
  contentPlaceholder: 'Write your private note...',
  saveNote: 'Save note',
  updateNote: 'Update note',
  cancel: 'Cancel',
  untitled: 'Untitled note',
  editAria: 'Edit note',
  deleteAria: 'Delete note',
  countOne: '1 note',
  countMany: '{count} notes',
  loadingLabel: 'Loading your private notes',
} as const;

/** Learner Certificates (`/certificates`). */
export const certificatesPage = {
  subtitle: 'Verified credentials earned from completed programs and assessments.',
  errorTitle: 'Unable to load your certificates',
  errorDescription: 'Something went wrong while loading your certificates. Please try again.',
  retry: 'Try again',
  countOne: '1 certificate',
  countMany: '{count} certificates',
  loadingLabel: 'Loading your certificates',
  verifyCta: 'Verify',
} as const;

export const programsPublic = {
  metaTitle: 'Programs',
  metaDescription:
    "Browse certificate, diploma, master's, and Ph.D programs from Takshify institutes.",
  heroEyebrow: 'Credential tracks',
  heroTitle: 'Programs',
  heroSubtitle:
    'Browse credential tracks and enroll. Coordinators review requests due to limited seats - you will get an email when approved.',
  filterLabel: 'Filter by program level',
  loadErrorTitle: 'Unable to load programs',
  loadError: 'Unable to load programs right now. Please try again later.',
  retry: 'Try again',
  emptyCategoryTitle: 'No programs in this category',
  emptyCategory: 'No programs in this category yet.',
  emptyCatalogTitle: 'Program catalog is being prepared',
  emptyCatalog: 'Program catalog is being prepared. Check back shortly.',
  signInCta: 'Sign in',
  loadingLabel: 'Loading programs',
  badgeOpen: 'Open',
  badgeEnrolling: 'Enrolling',
  enrollCta: 'Enroll me',
  syllabusCta: 'Syllabus',
  sendRequest: 'Send request',
  cancel: 'Cancel',
  saving: 'Saving…',
  emailLabel: 'Email',
  emailPlaceholder: 'you@example.com',
  signedInAs: 'Signed in as',
  formHint: 'Coordinator will review your request and email you.',
  successNew: 'Enrollment request received - a coordinator will review and email you.',
  successDuplicate: 'You already submitted an enrollment request for this program.',
  syllabusMoreTitle: 'Want the full path?',
  syllabusMoreBody:
    'This outline is only the trailhead. Lessons, live guidance, and the rest of the journey open after you enroll.',
  syllabusMoreCta: 'Send enroll request',
} as const;

export const common = {
  comingSoon: 'Premium · Coming Soon',
  loading: 'Loading...',
  error: 'Something went wrong',
  retry: 'Try again',
  save: 'Save',
  cancel: 'Cancel',
  delete: 'Delete',
  edit: 'Edit',
  search: 'Search',
  noResults: 'No results found',
  viewAll: 'View all',
} as const;

export const landing = {
  metaTitle: 'Learn Indian Knowledge Systems Online',
  metaDescription:
    'Takshify is a premium EdTech platform for Jyotish, Ayurveda, Yoga, Vedanta, and the full spectrum of Indian Knowledge Systems - taught by masters, powered by AI.',
  nav: {
    home: 'Home',
    courses: 'Courses',
    programs: 'Programs',
    aiGuru: 'AskGuru AI',
    certifications: 'Certifications',
    community: 'Community',
    about: 'About',
    contact: 'Contact',
    login: 'Sign in',
    searchPlaceholder: 'Search courses, lessons, schools...',
  },
  hero: {
    headline: 'Master timeless wisdom for the modern seeker',
    subheadline: 'Your marga to Indian Knowledge Systems starts here',
    description:
      'Structured paths in Jyotish, philosophy, wellness, and sacred sciences - with AI-guided study and a community of fellow Sadhaks.',
    ctaPrimary: 'Sign in to learn',
    ctaSecondary: 'Explore Learning Paths',
  },
  trustBadges: [
    { label: 'AI Powered' },
    { label: 'Authentic Knowledge' },
    { label: 'Structured Learning' },
    { label: 'Community' },
    { label: 'Certificates' },
  ],
  paths: {
    title: 'Featured Learning Paths',
    subtitle:
      'From classical Jyotish to Vedanta and beyond - choose your stream and begin when you are ready.',
    explore: 'Enroll Me',
  },
  whyLearn: {
    title: 'Why learn with Takshify',
    subtitle: 'Ancient wisdom deserves world-class structure - without losing the soul of the tradition.',
    features: [
      {
        title: 'Authentic Indian Knowledge Systems',
        description: 'Curricula rooted in classical texts, guru lineages, and time-tested frameworks.',
      },
      {
        title: 'AskGuru AI · 24×7',
        description:
          'Syllabus-grounded Jyotisha Q&A inspired by Maharishi Bhrigu - flashcards, revision summaries, and chart-lab guidance at any hour.',
      },
      {
        title: 'Structured learning paths',
        description: 'Clear milestones from foundations to mastery - no wandering, no guesswork.',
      },
      {
        title: 'Interactive quizzes & assessments',
        description: 'Test understanding with chapter quizzes, case studies, and revision queues.',
      },
      {
        title: 'Personalized recommendations',
        description: 'Study suggestions based on your progress, strengths, and learning goals.',
      },
      {
        title: 'Verified Certificates',
        description:
          'Institute-backed credentials with shareable verification codes - from Abhyas completion to diploma and postgraduate programs.',
      },
      {
        title: 'Progress tracking',
        description: 'See lessons completed, hours learned, and milestones across every path.',
      },
      {
        title: 'Guru–Sadhak Sangha',
        description:
          'Guru-led study circles, bilingual discussion channels, and weekly satsang-style threads where masters guide seekers and sadhaks learn together.',
      },
    ],
  },
  howItWorks: {
    title: 'How it works',
    subtitle: 'A clear journey from curiosity to mastery.',
    steps: [
      {
        title: 'Discover',
        description: 'Browse learning paths and find the stream that resonates with your calling.',
      },
      {
        title: 'Learn',
        description: 'Follow structured lessons with expert guidance and AI-powered support.',
      },
      {
        title: 'Practice',
        description: 'Apply knowledge through quizzes, charts, case studies, and daily sadhana.',
      },
      {
        title: 'Master',
        description: 'Earn certificates, deepen specialization, and join the community of scholars.',
      },
    ],
  },
  featuredCourses: {
    title: 'Featured courses',
    subtitle: 'Popular paths learners are starting right now.',
    viewCourse: 'View course',
  },
  stats: {
    title: 'Platform highlights',
    items: [
      { value: 18, suffix: '+', label: 'Learning paths' },
      { value: 169, suffix: '+', label: 'Interactive lessons' },
      { value: 24, suffix: '/7', label: 'AskGuru AI assistant' },
      { value: 500, suffix: '+', label: 'Practice questions' },
      { value: 1200, suffix: '+', label: 'Certificates issued' },
      { value: 8500, suffix: '+', label: 'Guru–Sadhak members' },
    ],
  },
  platformTeaser: {
    title: 'Platform tools',
    subtitle: 'AI study and sangha - explore each in depth.',
    learnMore: 'Learn more',
  },
  marketingPages: {
    askGuru: {
      eyebrow: 'Platform · AskGuru AI',
      metaTitle: 'AskGuru AI - Your Mentor All Along',
      metaDescription:
        'Syllabus-grounded Jyotisha mentorship inspired by Maharishi Bhrigu. Flashcards, chart lab, and revision support - study only, never fortune-telling.',
    },
    sangha: {
      eyebrow: 'Platform · Guru–Sadhak Sangha',
      metaTitle: 'Guru–Sadhak Sangha - Learn Together',
      metaDescription:
        'Guru-moderated channels, chart clinics, and weekly satsang threads. A digital gurukul for sadhaks across every school on Takshify.',
    },
    certifications: {
      eyebrow: 'Platform · Verified Credentials',
      metaTitle: 'Verified Certificates - Credentials That Honour Your Marga',
      metaDescription:
        'Institute-backed digital credentials with verification codes. From Abhyas completion through diploma and postgraduate programs.',
    },
  },
  communityShowcase: {
    title: 'The Guru–Sadhak Sangha',
    subtitle:
      'In the gurukul tradition, knowledge lived between guru and shishya - not in isolation. Takshify carries that spirit into a digital sangha where masters guide, sadhaks question, and peers walk the marga together.',
    sanskrit: 'एकं सद् विप्रा बहुधा वदन्ति',
    sanskritMeaning: 'Truth is one; the wise speak of it in many ways.',
    pillars: [
      {
        title: 'Guru-moderated channels',
        description:
          'Every school has its own study hall - Parashari, Jaimini, BNN, and more. Gurus and senior sadhaks keep discussions focused, respectful, and rooted in the syllabus.',
      },
      {
        title: 'Chart clinics & case threads',
        description:
          'Post a hora, ask about a dasha, debate a yoga - real charts, real questions. Learn by seeing how experienced readers think, not only by reading theory.',
      },
      {
        title: 'Weekly satsang circles',
        description:
          'Pinned threads and live-style discussions where a topic is explored in depth - nakshatra of the week, a classical shloka, or a learner\'s breakthrough moment.',
      },
      {
        title: 'Peer shishya network',
        description:
          'Seniors mentor juniors. Alumni return to answer beginners. The sangha grows wiser as each sadhak completes a milestone and pays it forward.',
      },
      {
        title: 'Bilingual discourse',
        description:
          'Hindi and English flow naturally - classical terms stay in Devanagari, explanations in the language you think in. No gatekeeping, no jargon walls.',
      },
      {
        title: 'Ethics-first dialogue',
        description:
          'Fortune-telling, fear-mongering, and sensational predictions have no place here. We study Jyotisha as shastra - with humility, responsibility, and care.',
      },
    ],
    channelsTitle: 'Active study halls',
    cta: 'Explore the sangha',
    ctaHint: 'Everything above is open to browse. Sign in to join channels and post.',
    signInLink: 'Sign in to join discussions',
  },
  askGuruShowcase: {
    title: 'AskGuru AI - your mentor all along',
    subtitle:
      'Three school guides. One AskGuru. Switch between Parashari, KP, and BNN - each persona answers only within that tradition\'s syllabus. Mentorship only, never fortune-telling.',
    sanskrit: 'ज्ञानं परमं बलम्',
    sanskritMeaning: 'Knowledge is the supreme strength.',
    personasTitle: 'Meet your AskGuru guides',
    personasSubtitle:
      'Pick a school. Watch a syllabus-grounded exchange with that guide - then sign in for live answers on Takshify.',
    pillars: [
      {
        title: 'Syllabus-grounded Q&A',
        description:
          'Every answer is anchored in Takshify lessons, classical frameworks, and BPHS - not generic internet astrology.',
      },
      {
        title: 'Chart Lab readouts',
        description:
          'Save a chart, ask about bhavas and dashas, and receive readouts tied to your syllabus progress.',
      },
      {
        title: 'Flashcards & quizzes',
        description:
          'Generate revision cards and practice questions from the chapter you are studying - active recall, not passive reading.',
      },
      {
        title: 'AskGuru, free and daily',
        description:
          'Five free answers per account, then up to 30 questions a day (20 on any single page) - no balance to track, no payments.',
      },
      {
        title: 'Bilingual guidance',
        description:
          'Hindi and English in the same calm voice - classical terms in Devanagari, explanations in the language you think in.',
      },
      {
        title: 'Ethics guardrails',
        description:
          'No death predictions, no medical or legal advice, no sensationalism - AskGuru refuses what shastra itself would not endorse.',
      },
    ],
    /** @deprecated Prefer personas[].conversation - kept for older teaser references */
    sampleConversation: [
      {
        question:
          'What does the 7th bhava signify in Parashari, and how do I begin reading marriage indications without over-predicting?',
        answer:
          'In BPHS tradition, the 7th bhava is kalatra - spouse, partnership, and how we meet the other. Start with the 7th lord\'s sign, house placement, and aspects - then divisional charts like D-9. AskGuru teaches the method; it does not predict who you will marry.',
      },
      {
        question:
          'How does the Navamsha (D-9) support 7th bhava analysis once I have the Rashi chart basics?',
        answer:
          'In Parashari, D-9 refines partnership quality: note the 7th lord in Navamsha, planets in the 7th from Navamsha lagna, and whether the Rashi 7th lord is vargottama. Layer Rashi promise with Navamsha strength - a structured comparison, not a fortune-telling shortcut.',
      },
    ],
    personas: [
      {
        id: 'parashari',
        schoolLabel: 'Parashari',
        guideName: 'Mah. Parashar',
        faceUrl: '/schools/parashari/assets/mah-parashar.png',
        tagline: 'BPHS · bhavas · dashas · vargas',
        canDo: [
          'Explain houses, lords, aspects, and yogas from a BPHS frame',
          'Walk Rashi → Navamsha without jumping to predictions',
          'Ground chart-lab questions in Parashari syllabus topics',
        ],
        thinking: 'Mah. Parashar is thinking…',
        conversation: [
          {
            question:
              'What does the 7th bhava signify in Parashari, and how do I begin reading marriage indications without over-predicting?',
            answer:
              'In BPHS tradition, the 7th bhava is kalatra - spouse, partnership, and how we meet the other. Start with the 7th lord\'s sign, house placement, and aspects - then divisional charts like D-9. AskGuru teaches the method; it does not predict who you will marry.',
          },
          {
            question:
              'How does the Navamsha (D-9) support 7th bhava analysis once I have the Rashi chart basics?',
            answer:
              'In Parashari, D-9 refines partnership quality: note the 7th lord in Navamsha, planets in the 7th from Navamsha lagna, and whether the Rashi 7th lord is vargottama. Layer Rashi promise with Navamsha strength - a structured comparison, not a fortune-telling shortcut.',
          },
        ],
      },
      {
        id: 'kp',
        schoolLabel: 'KP',
        guideName: 'Prof KP',
        faceUrl: '/schools/kp/assets/prof-kp.png',
        tagline: 'Cusps · sub-lords · significators',
        canDo: [
          'Teach cusp lore and sub-lord hierarchy step by step',
          'Map significators to a question without mixing Parashari rules',
          'Stay KP-only when you ask on KP lessons and tracks',
        ],
        thinking: 'Prof KP is thinking…',
        conversation: [
          {
            question:
              'In KP, how do I decide if a house is strong enough to deliver a result for a particular question?',
            answer:
              'In KP, start from the relevant cusp: note the cusp star lord and sub-lord, then list significators of the fructifying houses for that query. Strength is about ownership, occupation, and star/sub linkages - not a single Parashari yoga. Prof KP walks the significator chain; he does not promise an outcome date casually.',
          },
          {
            question:
              'What is the role of the sub-lord, and how is it different from the star lord?',
            answer:
              'The star lord shows the broader topic; the sub-lord narrows whether that topic can fructify for this chart and query. Read cusp → star → sub → linked houses as a sequence. That is Krishnamurti Paddhati method - precise cuspal logic, syllabus only, not open-ended prediction.',
          },
        ],
      },
      {
        id: 'bnn',
        schoolLabel: 'BNN',
        guideName: 'Mah. Bhrigu',
        faceUrl: '/schools/bnn/assets/mah-bhrigu.png',
        tagline: 'Karakatwas · transit · destinies',
        canDo: [
          'Explain Bhrigu Nandi Nadi karakatwas and planetary sambandha',
          'Frame transit timing as learning, not fear-based forecasts',
          'Keep answers inside BNN scope when you study that school',
        ],
        thinking: 'Mah. Bhrigu is thinking…',
        conversation: [
          {
            question:
              'In Bhrigu Nandi Nadi, how should I begin reading planetary karakatwas without confusing them with Parashari house lords?',
            answer:
              'BNN begins with the planet\'s inherent karakatwa and its relationship chain - not first with Parashari bhava lordship. Note each graha\'s promise, then how planets join or aspect in Nadi logic. Mah. Bhrigu teaches destinies through that lineage; he refuses mixing unrelated school rules into one answer.',
          },
          {
            question:
              'How do transit (gochar) readings work in BNN compared to a classical dasha-first approach?',
            answer:
              'In BNN, transit often activates the karaka story already present in the chart - read which karma threads light up when a planet moves, not only Vimshottari clocks. Study timing as syllabus pattern recognition: promise, link, then trigger. Learning only - never a sensational timeline of life events.',
          },
        ],
      },
      {
        id: 'lal-kitab',
        schoolLabel: 'Lal Kitab',
        guideName: 'Pt. Roop Chand',
        faceUrl: '/schools/lal-kitab/assets/roop-chand-joshi.png',
        tagline: 'Fixed houses · palmistry · Upayas',
        canDo: [
          'Teach fixed Aries-ascendant houses and Ghar Kaim logic',
          'Explain astro-palmistry, Pitri Rina, and household Upayas',
          'Stay Lal Kitab-only when you study that school',
        ],
        thinking: 'Pt. Roop Chand is thinking…',
        conversation: [
          {
            question:
              'In Lal Kitab, why is Aries always treated as the first house, and how should I start reading without mixing Parashari lagna rules?',
            answer:
              'Lal Kitab fixes Aries as house 1 for every chart - houses stay locked to signs, so you re-read the same birth data through Ghar Kaim rather than a moving lagna. Start with which planets sit in which fixed houses, then note debts and remedies. Pt. Roop Chand teaches that frame; he does not blend classical Parashari lagna lordship into the same answer unless you ask for a comparison.',
          },
          {
            question:
              'What makes Lal Kitab Upayas different from gemstone or mantra remedies in other schools?',
            answer:
              'Lal Kitab prefers simple household and symbolic Upayas - often for a set period like 43 days - over expensive gems or mantra prescription. Study the farmaan for the house and planet first, then the remedy as syllabus practice. Learning only: never a guarantee that a ritual will change life events.',
          },
        ],
      },
      {
        id: 'swara-vigyan',
        schoolLabel: 'Swara Vigyan',
        guideName: 'Mahadev',
        faceUrl: '/schools/swara-vigyan/assets/shiva-parvati.png',
        tagline: 'Ida · Pingala · nostril timing',
        canDo: [
          'Teach hand-check and Ida / Pingala / Sushumna observation',
          'Explain tithi rhythm and daily activity timing from Shiva Swarodaya',
          'Stay Swara Vigyan-only - ethics, not medicine or prediction',
        ],
        thinking: 'Mahadev is thinking…',
        conversation: [
          {
            question:
              'How do I check which nostril is active without forcing my breath or treating it as a medical test?',
            answer:
              'Use a gentle hand check: feel which nostril has more airflow after a normal exhale, without blocking or straining. Log what you feel versus what the syllabus table suggests at sunrise. Mahadev teaches observation for daily timing - not diagnosis, fear, or fortune-telling about health.',
          },
          {
            question:
              'What is the difference between Swara Vigyan and Pranayama in this school?',
            answer:
              'Swara Vigyan watches which nostril is open and picks a better moment for routine work. Pranayama is breath exercise - hold, count, strengthen. They can support each other, but this school stays with notice-and-plan, not prescription. Learning only.',
          },
        ],
      },
    ],
    sampleConversationLabel: 'Sample conversation',
    sampleIllustrative: 'Illustrative only - sign in for live syllabus-grounded answers.',
    sampleQuestion:
      'What does the 7th bhava signify in Parashari, and how do I begin reading marriage indications without over-predicting?',
    sampleAnswer:
      'In BPHS tradition, the 7th bhava is kalatra - spouse, partnership, and how we meet the other. Start with the 7th lord\'s sign, house placement, and aspects - then divisional charts like D-9. AskGuru teaches the method; it does not predict who you will marry.',
    signInLink: 'Sign in to ask your first question',
  },
  certificatesShowcase: {
    title: 'Credentials that honour your marga',
    subtitle:
      'A certificate on Takshify is not a participation badge - it is proof that you completed a rigorous syllabus, passed assessments, and earned recognition from institute partners who stand behind the credential.',
    tiersTitle: 'Credential pathways',
    tiers: [
      {
        level: 'Abhyas',
        title: 'School completion',
        description:
          'Finish the foundational or advanced syllabus of a school - Parashari, Jaimini, BNN, or Lal Kitab - and receive a verified completion credential.',
        example: 'Parashari Abhyas Certificate',
      },
      {
        level: 'Certificate',
        title: 'Institute certificate programs',
        description:
          'Short, focused programs validated by Takshify partner institutes - ideal for professionals adding Jyotisha competence to their practice.',
        example: 'Certificate in Vedic Astrology Foundations',
      },
      {
        level: 'Diploma',
        title: 'Structured diploma tracks',
        description:
          'Multi-term curricula with proctored assessments, chart practicums, and guru evaluation - the credential employers and clients can trust.',
        example: 'Diploma in Parashari Jyotisha',
      },
      {
        level: 'Advanced',
        title: 'PG Diploma · Master\'s · Ph.D',
        description:
          'Postgraduate and research pathways for scholars deepening specialization - seat-limited, institute-governed, and listed on our Programs page.',
        example: 'PG Diploma in Predictive Jyotisha',
      },
    ],
    featuresTitle: 'Why our credentials matter',
    features: [
      {
        title: 'Instant verification',
        description:
          'Every credential carries a unique code - anyone can confirm authenticity in seconds, without emailing the institute or waiting for a callback.',
      },
      {
        title: 'Assessment-backed',
        description:
          'Certificates are issued only after quizzes, practicums, and institute assessments - not merely for watching videos or clicking through lessons.',
      },
      {
        title: 'Shareable & professional',
        description:
          'Add credentials to your profile, share a verification link with clients, or present them to employers - your marga, made visible.',
      },
      {
        title: 'Institute partnership',
        description:
          'Takshify coordinates with partner institutes so credentials reflect real academic standards - not self-issued PDFs from an anonymous website.',
      },
    ],
    sampleCode: 'PLT-2026-PAR-0042',
    sampleHolder: 'Meera Krishnan',
    sampleTitle: 'Parashari Abhyas Certificate',
    sampleSchool: 'Parashari Jyotish',
    cta: 'Explore credential pathways',
    ctaHint: 'Sample credential shown for illustration. Sign in when you are ready to earn yours.',
    signInLink: 'Sign in to earn yours',
  },
  testimonials: {
    title: 'What learners say',
    subtitle: 'Stories from Sadhaks on the marga.',
  },
  faq: {
    title: 'Frequently asked questions',
    subtitle: 'Everything you need to know before you begin.',
  },
  finalCta: {
    title: 'Begin your marga today',
    subtitle:
      'Join thousands of learners discovering Indian Knowledge Systems with structure, depth, and an AI companion at your side.',
    ctaPrimary: 'Sign in to learn',
    ctaSecondary: 'Explore Programs',
  },
  footer: {
    tagline: 'The Digital Takshashila',
    sanskrit: 'सर्वे भवन्तु सुखिनः',
    columns: {
      platform: 'Platform',
      resources: 'Resources',
      legal: 'Legal',
    },
    links: {
      about: 'About Takshify',
      courses: 'Courses',
      programs: 'Programs',
      aiGuru: 'AskGuru AI',
      certifications: 'Certifications',
      blog: 'Blog',
      community: 'Community',
      contact: 'Contact',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
    },
  },
} as const;
